/**
 * Logo Aspect Ratio Helper
 * Calculates optimal logo dimensions while maintaining aspect ratio
 * Shared across template-editor and user-webapp
 */

// Cache for image dimensions to avoid redundant loading
const imageDimensionsCache = new Map();

/**
 * Clear the image dimensions cache
 * Call this when switching between projects or brands
 */
export function clearImageDimensionsCache() {
  imageDimensionsCache.clear();
  console.log("🗑️ Cleared image dimensions cache");
}

/**
 * Load an image and get its dimensions
 * @param {string} imageUrl - URL of the image to load
 * @returns {Promise<{width: number, height: number}>}
 */
export async function getImageDimensions(imageUrl) {
  // Check cache first
  if (imageDimensionsCache.has(imageUrl)) {
    console.log(
      "✅ Using cached image dimensions for:",
      imageUrl.substring(0, 50) + "..."
    );
    return imageDimensionsCache.get(imageUrl);
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // Enable CORS

    img.onload = () => {
      const dimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight
      };
      // Cache the dimensions
      imageDimensionsCache.set(imageUrl, dimensions);
      console.log("📐 Cached image dimensions:", dimensions);
      resolve(dimensions);
    };

    img.onerror = () => {
      console.warn("Failed to load image for dimension detection:", imageUrl);
      // Return default dimensions on error
      const defaultDimensions = { width: 200, height: 200 };
      // Cache even the default to avoid repeated failures
      imageDimensionsCache.set(imageUrl, defaultDimensions);
      resolve(defaultDimensions);
    };

    img.src = imageUrl;

    // Timeout after 5 seconds
    setTimeout(() => {
      if (!img.complete) {
        console.warn("Image loading timeout:", imageUrl);
        resolve({ width: 200, height: 200 });
      }
    }, 5000);
  });
}

/**
 * Calculate logo dimensions to fit within a container while maintaining aspect ratio
 * @param {number} naturalWidth - Original logo width
 * @param {number} naturalHeight - Original logo height
 * @param {number} containerWidth - Original template slot width
 * @param {number} containerHeight - Original template slot height
 * @param {number} maxWidth - Maximum allowed width
 * @param {number} maxHeight - Maximum allowed height
 * @returns {{width: number, height: number, x: number, offsetX: number}}
 */
export function calculateLogoFit(
  naturalWidth,
  naturalHeight,
  containerWidth,
  containerHeight,
  maxWidth = 300,
  maxHeight = 300
) {
  // Calculate aspect ratio
  const aspectRatio = naturalWidth / naturalHeight;

  let newWidth, newHeight;

  // Determine if logo is landscape, portrait, or square
  if (aspectRatio > 1) {
    // Landscape (wider than tall)
    newWidth = Math.min(containerWidth, maxWidth);
    newHeight = newWidth / aspectRatio;

    // If height exceeds container, scale down
    if (newHeight > containerHeight || newHeight > maxHeight) {
      newHeight = Math.min(containerHeight, maxHeight);
      newWidth = newHeight * aspectRatio;
    }
  } else if (aspectRatio < 1) {
    // Portrait (taller than wide)
    newHeight = Math.min(containerHeight, maxHeight);
    newWidth = newHeight * aspectRatio;

    // If width exceeds container, scale down
    if (newWidth > containerWidth || newWidth > maxWidth) {
      newWidth = Math.min(containerWidth, maxWidth);
      newHeight = newWidth / aspectRatio;
    }
  } else {
    // Square
    const size = Math.min(containerWidth, containerHeight, maxWidth, maxHeight);
    newWidth = size;
    newHeight = size;
  }

  // Calculate horizontal offset to center the logo
  const offsetX = (containerWidth - newWidth) / 2;

  return {
    width: Math.round(newWidth),
    height: Math.round(newHeight),
    offsetX: Math.round(offsetX),
    aspectRatio: aspectRatio.toFixed(2)
  };
}

/**
 * Adjust logo element to fit brand logo while maintaining aspect ratio and centering
 * @param {Object} logoElement - The Polotno logo element
 * @param {string} logoUrl - URL of the brand logo
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} - Updated logo element
 */
export async function adjustLogoElement(logoElement, logoUrl, options = {}) {
  const {
    maxWidth = 300,
    maxHeight = 300,
    centerHorizontally = true,
    keepRatio = true
  } = options;

  try {
    // Get the original container dimensions
    const containerWidth = logoElement.width || 200;
    const containerHeight = logoElement.height || 200;
    const originalX = logoElement.x || 0;

    // Load the logo image to get its natural dimensions
    const { width: naturalWidth, height: naturalHeight } =
      await getImageDimensions(logoUrl);

    console.log("🖼️ Logo dimensions:", {
      natural: { width: naturalWidth, height: naturalHeight },
      container: { width: containerWidth, height: containerHeight }
    });

    // Calculate optimal fit
    const { width, height, offsetX, aspectRatio } = calculateLogoFit(
      naturalWidth,
      naturalHeight,
      containerWidth,
      containerHeight,
      maxWidth,
      maxHeight
    );

    console.log("✅ Calculated logo fit:", {
      newSize: { width, height },
      aspectRatio,
      offsetX,
      centerHorizontally
    });

    // Return updated element
    return {
      ...logoElement,
      src: logoUrl,
      width,
      height,
      x: centerHorizontally ? originalX + offsetX : originalX,
      keepRatio: keepRatio,
      contentEditable: true
    };
  } catch (error) {
    console.error("Error adjusting logo element:", error);
    // Return original element with just the src updated
    return {
      ...logoElement,
      src: logoUrl
    };
  }
}

/**
 * Batch process multiple logo elements
 * @param {Array} elements - Array of Polotno elements
 * @param {string} logoUrl - URL of the brand logo
 * @param {Object} options - Additional options
 * @param {Object} options.precalculatedDimensions - Optional pre-calculated dimensions {width, height} to avoid loading image
 * @returns {Promise<Array>} - Updated elements array
 */
export async function processLogoElements(elements, logoUrl, options = {}) {
  if (!logoUrl) {
    return elements;
  }

  // Use pre-calculated dimensions if available, otherwise load the image
  let dimensions;
  if (
    options.precalculatedDimensions?.width &&
    options.precalculatedDimensions?.height
  ) {
    console.log(
      "✅ Using pre-calculated logo dimensions (no image load needed):",
      options.precalculatedDimensions
    );
    dimensions = {
      width: options.precalculatedDimensions.width,
      height: options.precalculatedDimensions.height
    };
  } else {
    console.log("⏳ Loading image to get dimensions...");
    dimensions = await getImageDimensions(logoUrl);
  }

  // Process all elements
  const processedElements = await Promise.all(
    elements.map(async (element) => {
      const slot = element.metadata?.slot || element.slot || "";

      if (slot.toLowerCase() === "logo" && element.type === "image") {
        // Calculate fit using pre-loaded dimensions
        const containerWidth = element.width || 200;
        const containerHeight = element.height || 200;
        const originalX = element.x || 0;

        const { width, height, offsetX } = calculateLogoFit(
          dimensions.width,
          dimensions.height,
          containerWidth,
          containerHeight,
          options.maxWidth || 300,
          options.maxHeight || 300
        );

        return {
          ...element,
          src: logoUrl,
          width,
          height,
          x:
            options.centerHorizontally !== false
              ? originalX + offsetX
              : originalX,
          keepRatio: options.keepRatio !== false,
          contentEditable: true
        };
      }

      return element;
    })
  );

  return processedElements;
}
